import { useState } from 'react';

import { useQuery } from '@tanstack/react-query';
import { match, P } from 'ts-pattern';

import { contactProfileOptions, ProfileDetails } from '@/entities/client';
import { useSheetStore } from '@/shared/stores/sheet-store';
import { CenteredSpinner } from '@/shared/ui/indicators/centered-spinner';
import { SheetDescription, SheetPanel, SheetTitle } from '@/shared/ui/panels/sheet-panel';

import { ContactDetailsSheetActionBar } from './contact-details-sheet/contact-details-sheet-action-bar';
import { ContactDetailsSheetHeading } from './contact-details-sheet/contact-details-sheet-heading';
import { ContactDetailsSheetRelationships } from './contact-details-sheet/contact-details-sheet-relationships';

export function ContactDetailsSheet() {
    const currentSheet = useSheetStore((state) => state.currentSheet);
    const liveContact =
        currentSheet?.type === 'clients/contact-details' ? currentSheet.data : undefined;
    // The store drops the contact on close; keep the last one so the sheet survives the slide-out.
    const [lastContact, setLastContact] = useState(liveContact);
    if (liveContact && liveContact.personId !== lastContact?.personId) {
        setLastContact(liveContact);
    }
    const contact = liveContact ?? lastContact;

    const {
        data: profile,
        isError,
        isFetching,
    } = useQuery({
        ...contactProfileOptions(contact?.personId ?? ''),
        enabled: !!liveContact?.personId,
    });
    // The cached profile may predate a change made from another chart, so every open waits for a
    // fetch, not only the first. The query is disabled once closed, so the slide-out keeps the last
    // profile. The action bar keys off `profile` so it stays put meanwhile.
    const isLoading = (!!liveContact && isFetching) || (!profile && !isError);
    const name = profile ? `${profile.firstName} ${profile.lastName}` : contact?.name;

    return (
        <SheetPanel
            type="clients/contact-details"
            side="right"
            size="medium"
            showCloseButton={false}
        >
            <ContactDetailsSheetActionBar
                contact={profile && { personId: profile.personId, name: name ?? '' }}
            />
            <div className="min-h-0 flex flex-col flex-1 overflow-y-auto">
                <SheetDescription className="sr-only">Contact profile for {name}.</SheetDescription>
                {match({ isLoading, profile })
                    .with({ isLoading: true }, () => (
                        <>
                            <SheetTitle className="sr-only">{name}</SheetTitle>
                            <CenteredSpinner label="Loading contact profile" />
                        </>
                    ))
                    .with({ profile: P.nonNullable }, ({ profile: loaded }) => (
                        <>
                            <ContactDetailsSheetHeading name={name ?? ''} profile={loaded} />
                            <div className="px-4 pb-6">
                                <ProfileDetails profile={loaded} />
                            </div>
                            <ContactDetailsSheetRelationships clients={loaded.clients} />
                        </>
                    ))
                    .otherwise(() => (
                        <>
                            <SheetTitle className="sr-only">{name}</SheetTitle>
                            <p className="p-4 text-sm text-muted-foreground">
                                Unable to load this contact. Try again later.
                            </p>
                        </>
                    ))}
            </div>
        </SheetPanel>
    );
}
